// buildMosaic — the framework-agnostic Mosaic orchestration: fetch the selected
// media from their presigned R2 URLs, decode the clips / load the photos into
// grid cells, and encode the grid to an MP4 via WebCodecs. The UI calls this; it
// stays free of React (so a SvelteKit shell could reuse it, ADR-0021), the way
// buildSheet.ts does for the contact Sheet.
//
// Reuses B3's gifDecode for clips (carrying the per-frame GCE delays the timeline
// needs) and loads photos as plain images. The deterministic grid/timeline lives
// in mosaic.ts (unit-tested); the encode is encodeMosaic.ts (WebCodecs, browser).

import { decodeGif, type GifFrame } from "./gifDecode";
import { makeClipCell, makeImageCell, type MosaicCell } from "./mosaicCell";
import {
  encodeMosaic,
  mosaicEncodeSupported,
  type MosaicProgress,
} from "./encodeMosaic";
import type { MosaicLayout } from "./mosaic";

/** A Clip is ≤ CLIP_MAX_FRAMES; web/mosaic.js decoded up to 80 to be safe. */
const MOSAIC_MAX_DECODE_FRAMES = 80;

/** One picked item: its presigned R2 URL and whether it's an animated Clip. */
export interface MosaicSelectionItem {
  /** The item's short-TTL presigned R2 GET URL. */
  readonly url: string;
  /** True for a Clip (.gif) — it animates; false for a photo (static cell). */
  readonly isClip: boolean;
  /**
   * A Clip's frames, if the caller already decoded them. The overlay decodes each clip
   * once on open (to learn the longest loop for its length stepper) and hands the frames
   * in here, so Encode neither re-fetches nor re-decodes — and stays immune to a
   * short-TTL URL that expired while the overlay sat open (the ContactSheet's #138 idiom).
   */
  readonly frames?: readonly GifFrame[];
}

/** Injectable seams so the flow is testable without a real network / DOM image. */
export interface BuildMosaicDeps {
  fetch?: typeof fetch;
  /** Load an <img> from a URL (default: a real HTMLImageElement). */
  loadImage?: (url: string) => Promise<CanvasImageSource>;
}

export interface BuildMosaicOptions {
  readonly layout: MosaicLayout;
  /** Explicit grid column count (the UI's stepper); overrides the layout heuristic. */
  readonly cols?: number;
  /** Whole loops of the base duration to play (the UI's length stepper; default 1). */
  readonly loops?: number;
  readonly onProgress?: MosaicProgress;
  /** Per-item load progress (`done`, `total`) before encoding starts. */
  readonly onLoad?: (done: number, total: number) => void;
  /** Abort the build (Cancel) — checked between items, threaded to fetch + the encode. */
  readonly signal?: AbortSignal;
  /** How many tiles survived vs were requested, reported before encoding ("Tiled N of M"). */
  readonly onTiled?: (tiled: number, requested: number) => void;
}

/**
 * Fetch + decode the selection, then encode the Mosaic to an MP4 Blob.
 *
 * Items load in pick order (= cell order). A clip that fails to fetch/decode is
 * skipped (best-effort, like web/mosaic.js); if nothing survives, this throws.
 * WebCodecs support is checked up front so the caller can show a clear message.
 *
 * @throws "WebCodecs is unavailable…" when the browser can't H.264-encode.
 * @throws "Could not read the selection." when no item decodes.
 */
export async function buildMosaicFromSelection(
  items: readonly MosaicSelectionItem[],
  options: BuildMosaicOptions,
  deps: BuildMosaicDeps = {},
): Promise<Blob> {
  if (items.length === 0) throw new Error("Nothing to tile.");
  if (!(await mosaicEncodeSupported())) {
    throw new Error(
      "Mosaic needs WebCodecs (H.264) video encoding, which this browser doesn’t support.",
    );
  }

  const doFetch = deps.fetch ?? globalThis.fetch.bind(globalThis);
  const loadImage = deps.loadImage ?? defaultLoadImage;

  const cells: MosaicCell[] = [];
  for (let i = 0; i < items.length; i++) {
    if (options.signal?.aborted) throw new DOMException("Mosaic build aborted", "AbortError");
    const item = items[i]!;
    try {
      if (item.isClip) {
        let frames = item.frames;
        if (!frames) {
          const res = await doFetch(item.url, { signal: options.signal });
          if (!res.ok) throw new Error(`fetch failed (${res.status})`);
          const buffer = await res.arrayBuffer();
          frames = decodeGif(buffer, MOSAIC_MAX_DECODE_FRAMES);
        }
        if (frames.length > 0) cells.push(makeClipCell(frames));
      } else {
        cells.push(makeImageCell(await loadImage(item.url)));
      }
    } catch (e) {
      // An abort propagates; anything else is best-effort skipped (keep the rest).
      if (options.signal?.aborted || (e as Error)?.name === "AbortError") throw e;
    }
    options.onLoad?.(i + 1, items.length);
  }

  if (cells.length === 0) throw new Error("Could not read the selection.");

  // Surface how many tiles survived vs were requested, so the UI can say "Tiled N of M".
  options.onTiled?.(cells.length, items.length);

  return encodeMosaic(cells, {
    layout: options.layout,
    cols: options.cols,
    loops: options.loops,
    onProgress: options.onProgress,
    signal: options.signal,
  });
}

/** Default image loader — a real HTMLImageElement (DOM-only). */
function defaultLoadImage(url: string): Promise<CanvasImageSource> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous"; // presigned R2 URL is cross-origin; allow canvas use
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image load failed"));
    img.src = url;
  });
}
